import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowForward } from '@mui/icons-material';
import './HomeHero.css';

const HomeCTA: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Set video to loop and muted
    video.muted = true;
    video.loop = true;

    // Try to play the video
    const playVideo = async () => {
      try {
        await video.play();
      } catch (error) {
        console.log('Video autoplay error:', error);
        // Try again on user interaction
        const handleInteraction = () => {
          video.play().catch(e => console.log('Play failed:', e));
          document.removeEventListener('click', handleInteraction);
          document.removeEventListener('touchstart', handleInteraction);
        };
        document.addEventListener('click', handleInteraction);
        document.addEventListener('touchstart', handleInteraction);
      }
    };

    // Wait for video metadata to load
    if (video.readyState >= 2) {
      playVideo();
    } else {
      video.addEventListener('loadedmetadata', playVideo);
      video.addEventListener('canplay', playVideo);
    }

    return () => {
      video.removeEventListener('loadedmetadata', playVideo);
      video.removeEventListener('canplay', playVideo);
    };
  }, []);

  return (
    <section className="relative min-h-[400px] sm:min-h-[500px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover'
          }}
        >
          <source src="/videos/cta section video.webm" type="video/webm" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black/50 z-10"></div>
        {/* Grid Overlay - 4 vertical lines and 2 horizontal lines */}
        <div 
          className="absolute inset-0 z-[15] pointer-events-none"
        >
          {/* 4 Vertical Lines */}
          <div 
            className="absolute top-0 bottom-0 w-px bg-white"
            style={{ left: '20%', opacity: 0.16 }}
          ></div>
          <div 
            className="absolute top-0 bottom-0 w-px bg-white"
            style={{ left: '40%', opacity: 0.16 }}
          ></div>
          <div 
            className="absolute top-0 bottom-0 w-px bg-white"
            style={{ left: '60%', opacity: 0.16 }}
          ></div>
          <div 
            className="absolute top-0 bottom-0 w-px bg-white"
            style={{ left: '80%', opacity: 0.16 }}
          ></div>
          {/* 2 Horizontal Lines */}
          <div 
            className="absolute left-0 right-0 h-px bg-white"
            style={{ top: '33.333%', opacity: 0.16 }}
          ></div>
          <div 
            className="absolute left-0 right-0 h-px bg-white"
            style={{ top: '66.666%', opacity: 0.16 }}
          ></div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-20">
        <div className="text-center max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            style={{ 
              fontFamily: "'Montserrat', sans-serif",
              color: '#EF3F23'
            }}
          >
            Discover Our Creative Projects
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-white mb-8 leading-relaxed"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            From documentaries to branding campaigns, see the power of our storytelling. We use unique storytelling techniques that help to tell our clients' stories in a meaningful way that makes everyone proud.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex justify-center"
          >
            <Link
              to="/work"
              className="button-hover group relative inline-flex items-center px-6 py-3 rounded-lg font-headline font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
              style={{ 
                border: '2px solid #EF3F23',
                color: '#EF3F23',
                backgroundColor: 'transparent'
              }}
            >
              <span className="button-text flex items-center gap-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                View Our Portfolio
                <ArrowForward style={{ fontSize: '14px' }} />
              </span>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Tagline */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="absolute bottom-4 right-4 md:bottom-8 md:right-8 z-20"
      >
        <p
          className="text-sm sm:text-base md:text-lg lg:text-xl"
          style={{ 
            fontFamily: "'HolidayFree', sans-serif",
            color: '#EF3F23',
            opacity: 0.7
          }}
        >
          documenting real life
        </p>
      </motion.div>
    </section>
  );
};

export default HomeCTA;
